/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { useState } from 'react';
import { BirthDateField } from '../../../../../core/types/reservation-form-types';
import { FieldErrorMessage } from '../FieldErrorMessage';
import { BaseFormFieldProps } from './BaseFormField';
import { BirthDateInput } from './BirthDateInput';
import { FieldDescription } from './FieldDescription';
import { FieldLabel } from './FieldLabel';
import { buildFieldValidation } from './input-helpers';
import { styles as inputStyles } from './input-styles';

type BirthDateFormFieldProps = BaseFormFieldProps & {
  field: BirthDateField;
};

export function BirthDateFormField(props: BirthDateFormFieldProps) {
  const { field, fieldResponse, onChangeValue, validationContext } = props;
  const { errors, register } = validationContext;
  const [invalidDate, setInvalidDate] = useState(false);

  const fieldValidation = {
    ...buildFieldValidation(field),
    validate: {
      validDate: (value: string) => {
        if (invalidDate) {
          return '無効な日付です';
        }
        if (!value) return true; // 空の場合はOK（必須チェックは別で行われる）

        // 日付形式のチェック
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (!datePattern.test(value)) {
          return '正しい日付形式で入力してください（YYYY-MM-DD）';
        }

        // 日付の各部分を取得
        const [yearStr, monthStr, dayStr] = value.split('-');
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const day = parseInt(dayStr, 10);

        // 有効な日付かチェック（タイムゾーンの影響を受けない方法）
        const isValidDate = (y: number, m: number, d: number): boolean => {
          // 月は0-11の範囲で指定する必要がある
          const date = new Date(y, m - 1, d);
          return (
            date.getFullYear() === y &&
            date.getMonth() === m - 1 &&
            date.getDate() === d
          );
        };

        if (!isValidDate(year, month, day)) {
          return '有効な日付を入力してください';
        }

        // 未来の日付はエラー
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inputDate = new Date(year, month - 1, day);
        if (inputDate > today) {
          return '未来の日付は入力できません';
        }

        return true;
      },
    },
  };

  // バリデーションエラーハンドラ
  const handleValidationError = (message: string) => {
    validationContext.setError(field.uid, {
      type: 'manual',
      message,
    });
  };

  // バリデーションエラーをクリア
  const handleClearValidationError = () => {
    validationContext.clearErrors(field.uid);
  };

  // 日付変更ハンドラ
  const handleDateChange = (value: string) => {
    onChangeValue?.(field, value ? [value] : []);
  };

  const fieldError = Boolean(errors[field.uid]);

  return (
    <div>
      <div css={inputStyles.inputLabel}>
        <div css={inputStyles.inputTitle}>
          <FieldLabel field={field} />
        </div>
        <FieldErrorMessage field={field} errors={errors} />
      </div>
      <FieldDescription description={field.description} />

      <div>
        {/* submit時のエラーハンドリング用 hidden */}
        <input
          type="hidden"
          name={field.uid}
          ref={register(fieldValidation)}
          value={fieldResponse.values[0] as string}
        />
        <BirthDateInput
          value={(fieldResponse.values[0] as string) || ''}
          onChange={handleDateChange}
          onUpdateInvalidDate={setInvalidDate}
          fieldError={!!fieldError}
          field={field}
          onValidationError={handleValidationError}
          onClearError={handleClearValidationError}
        />
      </div>
    </div>
  );
}

const styles = {
  inputLabel: css`
    font-size: 12px;
    margin-bottom: 4px;
  `,
};
